import { CommonModule } from '@angular/common';
import { Component, effect, inject, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { CheckboxFieldComponent } from '../form';
import { DatepickerFieldComponent } from '../form/datepicker-field/datepicker-field.component';
import { ModalType } from './enums/modal-type.enum';
import { SmartModalAction } from './enums/smart-modal-action.enum';
import { CustomModalOptions } from './model/modal-options.model';

/**
 * The data shape the smart modal itself understands, for the optional
 * "data evento" confirmation field. Callers may pass additional keys used
 * only for message interpolation.
 */
export interface SmartModalData {
  dataEvento?: Date | string | null;
  showDataEvento?: boolean;
  [key: string]: unknown;
}

@Component({
  selector: 'siat-smart-modal',
  templateUrl: './smart-modal.component.html',
  styleUrls: ['./smart-modal.component.scss'],

  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    CheckboxFieldComponent,
    DatepickerFieldComponent,
  ],
})
export class SmartModalComponent {
  /**
   * The smart modal type.
   */
  type = signal<ModalType>(ModalType.SUCCESS);

  /**
   * The enum it contains the modal type.
   */
  modalType = ModalType;

  /**
   * The enum it contains the modal action type.
   */
  modalAction = SmartModalAction;

  /**
   * The custom modal options.
   */
  modalOptions = signal<CustomModalOptions | undefined>(undefined);

  readonly today = new Date();

  /**
   * The active modal reference.
   */
  activeModal = inject(MatDialogRef);

  readonly checkActionForm = new FormGroup<{
    flag: FormControl<boolean | null>;
  }>({
    flag: new FormControl<boolean | null>(false, []),
  });

  readonly dataEventoForm = new FormGroup<{
    dataEvento: FormControl<Date | null>;
  }>({
    dataEvento: new FormControl<Date | null>(new Date(), []),
  });

  private readonly syncDataEventoForm = effect(() => {
    const data = this.modalOptions()?.data as SmartModalData | undefined;
    const dataEvento = this.normalizeDataEvento(data?.dataEvento);
    this.dataEventoForm.patchValue({ dataEvento }, { emitEvent: false });
  });

  get showDateEventoField(): boolean {
    const data = this.modalOptions()?.data as SmartModalData | undefined;
    return !!data?.showDataEvento;
  }

  private normalizeDataEvento(value: unknown): Date {
    const parsed = value instanceof Date ? value : new Date(value as never);

    if (Number.isNaN(parsed.getTime())) {
      return new Date();
    }

    return parsed > this.today ? this.today : parsed;
  }

  /**
   * Triggers the click on button and close the active modal.
   */
  closeModal(modalAction: SmartModalAction): void {
    if (modalAction === SmartModalAction.CONFIRMED && this.showDateEventoField) {
      this.modalOptions.update(options => ({
        ...options,
        data: {
          ...(options?.data as SmartModalData | undefined),
          dataEvento: this.dataEventoForm.value?.dataEvento ?? null,
        },
      }));
    }

    const action =
      this.type() === this.modalType.ACTION && this.checkActionForm.value?.flag
        ? this.modalAction.CHECKED
        : modalAction;
    this.activeModal.close(action);
  }
}
